/**
 * @fileoverview Grid of effective manifest relationships with edit/delete affordances.
 * @module system-web/components/migration/manifest-editor/ManifestRelationshipsGrid
 *
 * Responsibilities:
 * - Render all relationships from the effective manifest (baseline + override applied).
 * - Show enforcement and orphan-policy badges per relationship.
 * - Provide Edit and Delete (disable) affordances that bubble up to the parent.
 * - Support adding a new manifest-only relationship via an "Add Relationship" button.
 *
 * Dependencies:
 * - lucide-react (icons)
 *
 * Architecture:
 * - Presentation only: all mutation decisions are delegated to parent via callbacks.
 * - Receives the effective manifest relationships already computed by resolveEffectiveManifest.
 *
 * Notes:
 * - Baseline relationships show their enforcement source badge.
 * - manifest-only added relationships show a "Manifest" enforcement badge.
 * - Disabled relationships (enabled: false via modification) are shown with a strikethrough.
 *
 * @reusable
 * Usage: Rendered inside ManifestEditorTab alongside ManifestTablesGrid.
 * Rules: presentation-only; stateless except for row-level hover.
 */

import React from 'react';
import { Link2, Edit2, Trash2, Plus, AlertTriangle } from 'lucide-react';

// ---------------------------------------------------------------------------
// Contracts
// ---------------------------------------------------------------------------

/**
 * @contract EffectiveRelationship
 *
 * Purpose:
 * - A relationship row for the grid, derived from the effective manifest.
 *
 * Fields:
 * - matchKey: 'parentTable.parentCol -> childTable.childCol' (unique identifier)
 * - parent: { table, column }
 * - child: { table, column }
 * - cardinality: 'one-to-one' | 'one-to-many'
 * - enforcement: 'declared' | 'convention' | 'inferred-by-name' | 'manifest-only'
 * - orphanPolicy: 'block' | 'warn' | 'drop' | 'includeAsRoot'
 * - orphanPolicyJustification?: string
 * - isDisabled: true if this relationship has been disabled by a modifiedRelationships entry
 * - isModified: true if this relationship has a modifiedRelationships entry (even if enabled)
 * - isAdded: true if this is an addedRelationships entry (manifest-only)
 *
 * Rules:
 * - matchKey is stable across renders (not an index).
 *
 * Sync:
 * - Derived from resolveEffectiveManifest output; not persisted.
 */
export interface EffectiveRelationship {
  matchKey: string;
  parent: { table: string; column: string };
  child: { table: string; column: string };
  cardinality: 'one-to-one' | 'one-to-many';
  enforcement: 'declared' | 'convention' | 'inferred-by-name' | 'manifest-only';
  orphanPolicy: 'block' | 'warn' | 'drop' | 'includeAsRoot';
  orphanPolicyJustification?: string;
  isDisabled: boolean;
  isModified: boolean;
  isAdded: boolean;
}

/**
 * @contract ManifestRelationshipsGridProps
 *
 * Purpose:
 * - Props for the ManifestRelationshipsGrid component.
 *
 * Fields:
 * - relationships: Effective manifest relationships to display.
 * - onEdit: Callback when the Edit button is clicked for a relationship.
 * - onDisable: Callback when the Disable (trash) button is clicked.
 * - onAddNew: Callback when the "Add Relationship" button is clicked.
 *
 * Rules:
 * - isAdded relationships can be deleted (removed from addedRelationships).
 * - Baseline relationships can only be disabled via modifiedRelationships (enabled: false).
 *
 * Sync:
 * - Not applicable.
 */
export interface ManifestRelationshipsGridProps {
  relationships: EffectiveRelationship[];
  onEdit: (rel: EffectiveRelationship) => void;
  onDisable: (rel: EffectiveRelationship) => void;
  onAddNew: () => void;
}

// ---------------------------------------------------------------------------
// Badge helpers
// ---------------------------------------------------------------------------

const ENFORCEMENT_CONFIG: Record<string, { label: string; bg: string; color: string }> = {
  'declared':         { label: 'Declared FK',     bg: 'rgba(37,99,235,0.1)',  color: '#1d4ed8' },
  'convention':       { label: 'Convention',       bg: 'rgba(22,163,74,0.1)', color: '#15803d' },
  'inferred-by-name': { label: 'Inferred',         bg: 'rgba(217,119,6,0.1)', color: '#b45309' },
  'manifest-only':    { label: 'Manifest',         bg: 'rgba(124,58,237,0.1)', color: '#6d28d9' },
};

const ORPHAN_CONFIG: Record<string, { label: string; color: string }> = {
  block:          { label: 'Block',        color: '#15803d' },
  warn:           { label: 'Warn',         color: '#b45309' },
  drop:           { label: 'Drop',         color: '#dc2626' },
  includeAsRoot:  { label: 'Include Root', color: '#6d28d9' },
};

function EnforcementBadge({ enforcement }: { enforcement: string }) {
  const cfg = ENFORCEMENT_CONFIG[enforcement] ?? ENFORCEMENT_CONFIG['convention'];
  return (
    <span
      className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium"
      style={{ background: cfg.bg, color: cfg.color }}
    >
      {cfg.label}
    </span>
  );
}

function OrphanBadge({ policy, justification }: { policy: string; justification?: string }) {
  const cfg = ORPHAN_CONFIG[policy] ?? ORPHAN_CONFIG['block'];
  return (
    <span
      className="inline-flex items-center gap-0.5 text-xs font-medium"
      style={{ color: cfg.color }}
      title={justification ?? undefined}
    >
      {cfg.label}
      {justification && <AlertTriangle className="h-2.5 w-2.5 opacity-70" />}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * @component ManifestRelationshipsGrid
 *
 * Purpose:
 * - Display all effective manifest relationships for the active dataset, with enforcement
 *   + orphan-policy badges and edit/disable affordances.
 *
 * Props:
 * - See ManifestRelationshipsGridProps.
 *
 * Behavior:
 * - Each row shows parent→child arrow, cardinality, enforcement badge, orphan-policy badge.
 * - Edit button opens EditRelationshipModal in parent.
 * - Disable/Delete button calls onDisable; parent writes to modifiedRelationships (enabled: false)
 *   for baseline rels, or removes from addedRelationships for manifest-only rels.
 * - Disabled rows are rendered with strikethrough styling and no action buttons.
 * - "Add Relationship" button at the bottom calls onAddNew.
 *
 * Data Source:
 * - Derived from effective manifest (passed as props); no data fetching.
 *
 * Constraints:
 * - No Dexie reads or writes; no API calls.
 *
 * Performance:
 * - List is typically small (2–10 relationships); no virtualization needed.
 *
 * @reusable
 * Usage: One per active dataset inside ManifestEditorTab.
 * Rules: presentation-only; mutations delegated to parent.
 */
export function ManifestRelationshipsGrid({
  relationships,
  onEdit,
  onDisable,
  onAddNew,
}: ManifestRelationshipsGridProps) {
  return (
    <div className="space-y-2">
      <div className="rounded-lg border overflow-hidden" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
        {relationships.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-8 text-sm text-gray-400">
            <Link2 className="h-6 w-6 opacity-30" />
            <span>No relationships in this manifest.</span>
          </div>
        ) : (
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b bg-gray-50" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide opacity-60">Relationship</th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide opacity-60 w-24">Enforcement</th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide opacity-60 w-28">Orphan Policy</th>
                <th className="px-3 py-2 w-20" />
              </tr>
            </thead>
            <tbody>
              {relationships.map((rel) => (
                <tr
                  key={rel.matchKey}
                  className="border-b transition-colors hover:bg-gray-50"
                  style={{
                    borderColor: 'rgba(0,0,0,0.06)',
                    opacity: rel.isDisabled ? 0.4 : 1,
                  }}
                >
                  {/* Relationship expression */}
                  <td className="px-3 py-2">
                    <span
                      className="font-mono text-xs"
                      style={{ textDecoration: rel.isDisabled ? 'line-through' : undefined }}
                    >
                      <span className="text-gray-600">{rel.parent.table}</span>
                      <span className="text-gray-400 mx-1">.</span>
                      <span className="font-semibold">{rel.parent.column}</span>
                      <span className="mx-1.5 text-gray-400">→</span>
                      <span className="text-gray-600">{rel.child.table}</span>
                      <span className="text-gray-400 mx-1">.</span>
                      <span className="font-semibold">{rel.child.column}</span>
                    </span>
                    <span className="ml-2 text-xs text-gray-400">
                      {rel.cardinality === 'one-to-many' ? '1:N' : '1:1'}
                    </span>
                    {rel.isModified && !rel.isAdded && (
                      <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-50 text-amber-700">
                        Modified
                      </span>
                    )}
                    {rel.isAdded && (
                      <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-50 text-purple-700">
                        Added
                      </span>
                    )}
                  </td>

                  {/* Enforcement badge */}
                  <td className="px-3 py-2">
                    <EnforcementBadge enforcement={rel.enforcement} />
                  </td>

                  {/* Orphan policy */}
                  <td className="px-3 py-2">
                    <OrphanBadge policy={rel.orphanPolicy} justification={rel.orphanPolicyJustification} />
                  </td>

                  {/* Actions */}
                  <td className="px-3 py-2">
                    {!rel.isDisabled && (
                      <div className="flex items-center gap-1 justify-end">
                        <button
                          onClick={() => onEdit(rel)}
                          title="Edit relationship"
                          className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-700"
                        >
                          <Edit2 className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => onDisable(rel)}
                          title={rel.isAdded ? 'Remove added relationship' : 'Disable this relationship'}
                          className="p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-600"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="flex justify-end">
        <button
          onClick={onAddNew}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border rounded-lg hover:bg-gray-50"
        >
          <Plus className="h-3.5 w-3.5" />
          Add Relationship
        </button>
      </div>
    </div>
  );
}
